import { Fragment, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import PaginationControls from "../boards/PaginationControls";
import PlayerLink from "../common/PlayerLink";
import type { PaginationMetadata } from "../../types/boards";
import type {
  EpicLeaderboardRow,
  EpicLeaderboardSort
} from "../../types/epicLeaderboard";
import BossChips from "./BossChips";
import { EpicRunNotePanel, EpicRunNoteToggle } from "./EpicRunNote";
import { formatDate } from "./format";
import { analytics } from "../../utils/analytics";
import "./epicLeaderboard.css";

type Props = {
  rows: EpicLeaderboardRow[];
  pagination: PaginationMetadata;
  sort: EpicLeaderboardSort;
  refetching: boolean;
  onPageChange: (page: number) => void;
};

const activeClass = (active: boolean) => (active ? "epic-lb-active-col" : "");

// Rank, Player, Fastest, Average, Runs, Last run.
const COLUMN_COUNT = 6;

const EpicLeaderboardTable = ({
  rows,
  pagination,
  sort,
  refetching,
  onPageChange
}: Props) => {
  // Player whose fastest-run note is open; one at a time. Several rows can
  // share a run, so it's keyed by row rather than by run.
  const [openPlayer, setOpenPlayer] = useState<string | null>(null);

  // A new page/filter replaces the rows; don't carry an open note over.
  useEffect(() => setOpenPlayer(null), [rows]);

  const toggleNote = (row: EpicLeaderboardRow) => {
    if (openPlayer === row.player) {
      setOpenPlayer(null);
      return;
    }
    analytics.trackFeatureUsage("EpicLeaderboard", "note_open", {
      runId: row.fastestRunId
    });
    setOpenPlayer(row.player);
  };

  return (
    <div className="epic-lb-table-wrap">
      <div className={refetching ? "epic-lb-refetching" : undefined}>
        <div className="epic-lb-table-scroll">
          <table className="epic-lb-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Player</th>
                <th className={activeClass(sort === "fastest")}>Fastest</th>
                <th
                  className={`d-none d-md-table-cell ${activeClass(
                    sort === "average"
                  )}`}
                >
                  Average
                </th>
                <th className={activeClass(sort === "runs")}>Runs</th>
                <th className="d-none d-md-table-cell">Last run</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Fragment key={row.player}>
                  <tr>
                    <td>
                      <span
                        className={`epic-lb-rank${
                          row.rank <= 3 ? ` epic-lb-rank--${row.rank}` : ""
                        }`}
                      >
                        {row.rank}
                      </span>
                    </td>
                    <td>
                      <PlayerLink
                        player={row.player}
                        className="epic-lb-player-link"
                      />
                    </td>
                    <td className={activeClass(sort === "fastest")}>
                      <div className="fw-bold">{row.fastest}</div>
                      <div className="epic-lb-teammates">
                        {formatDate(row.fastestRunDate)}
                        {row.fastestTeammates.length > 0 && (
                          <span title={row.fastestTeammates.join(", ")}>
                            {" "}
                            - with {row.fastestTeammates.join(", ")}
                          </span>
                        )}
                      </div>
                      <BossChips bosses={row.fastestRunBosses} />
                      <EpicRunNoteToggle
                        note={row.fastestRunNote}
                        open={openPlayer === row.player}
                        onToggle={() => toggleNote(row)}
                      />
                    </td>
                    <td
                      className={`d-none d-md-table-cell ${activeClass(
                        sort === "average"
                      )}`}
                    >
                      {row.average}
                    </td>
                    <td className={activeClass(sort === "runs")}>
                      {row.runCount.toLocaleString()}
                    </td>
                    <td className="d-none d-md-table-cell">
                      {formatDate(row.lastRunDate)}
                    </td>
                  </tr>
                  {openPlayer === row.player && (
                    <tr className="epic-lb-note-row">
                      <td colSpan={COLUMN_COUNT} className="epic-lb-note-cell">
                        <div className="epic-lb-note-cell-inner">
                          <EpicRunNotePanel runId={row.fastestRunId} />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.totalPages > 1 && (
          <PaginationControls
            pagination={pagination}
            onPageChange={onPageChange}
          />
        )}
      </div>
      {refetching && (
        <div className="epic-lb-overlay">
          <Spinner animation="border" variant="light" />
        </div>
      )}
    </div>
  );
};

export default EpicLeaderboardTable;
